import { useState } from 'react';
import { Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import StarRating from '@/components/StarRating';
import { cn } from '@/lib/utils';

export const TestimonialModal = ({ testimonial, open, onOpenChange }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = testimonial?.image_urls || [];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (!testimonial) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-3xl bg-[#0a0a0a] border-white/10 p-0 overflow-hidden"
        data-testid="testimonial-modal"
      >
        {images.length > 0 && (
          <div className="relative bg-black">
            <img
              src={images[currentImageIndex]}
              alt={`${testimonial.title} - Image ${currentImageIndex + 1}`}
              className="w-full h-auto max-h-[50vh] object-contain"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/600x400?text=Image+Not+Available';
              }}
            />
            
            {images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                  data-testid="prev-image-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                  data-testid="next-image-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
                
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={cn(
                        'w-2 h-2 rounded-full transition-all',
                        idx === currentImageIndex
                          ? 'bg-white w-6'
                          : 'bg-white/40 hover:bg-white/60'
                      )}
                      data-testid={`image-indicator-${idx}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        
        <div className="p-6">
          <DialogHeader className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <StarRating rating={testimonial.rating} size="large" />
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <Clock className="w-4 h-4" />
                <span className="mono">{testimonial.time_to_finish}</span>
              </div>
            </div>
            <DialogTitle className="text-xl font-bold text-white">
              {testimonial.title}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Project details and gallery
            </DialogDescription>
          </DialogHeader>
          
          <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">
            {testimonial.description}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TestimonialModal;
